/**
 * =============================================================================
 * UnifiedEpubRenderer — epub.js-based reader (one of TWO EPUB renderers)
 * =============================================================================
 *
 * SIBLING: UnifiedCustomHtmlRenderer.tsx (backend-preprocessed HTML + ShadowDOM).
 * ROUTING: BookReaderPage.tsx picks based on book.rendererType.
 *
 * When is THIS renderer used?
 *   For complex EPUBs that the simpler CustomHtml path can't handle correctly:
 *     - Fixed-layout EPUBs (rendition:layout=pre-paginated): graphic novels,
 *       picture books, heavily illustrated textbooks.
 *     - EPUB3 with interactive scripts / widgets.
 *     - Media overlays (audio-text sync for accessibility / language learning).
 *     - Precise CFI navigation for scholarly citation.
 *     - RTL or vertical-text content (Arabic, Hebrew, Japanese vertical).
 *   Intended share: ~5-10% of the corpus (see DrmQuotas/README.md).
 *
 * Current state:
 *   Auto-routing is NOT YET implemented. See EpubComplexityAnalyzer.cs — it is
 *   a stub that forces every book to CustomHtml for now. Admins can flip a
 *   book to this renderer manually via the management API.
 *
 * ⚠️  WHEN ADDING FEATURES, UPDATE BOTH RENDERERS ⚠️
 *   Any reader feature (layout controls, highlights, bookmarks, search, TTS,
 *   settings, etc.) must be wired into BOTH this file and
 *   UnifiedCustomHtmlRenderer.tsx, or the experience diverges between book
 *   types. Same goes for NavigationState/ReaderSettings type changes.
 * =============================================================================
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ePub, { Book, Rendition } from 'epubjs';
import { UnifiedReaderLayout } from './index';
import type {
  NavigationState,
  NavigationCallbacks,
  ReaderActions,
  TOCItem,
  Bookmark,
  Note,
  SearchResult,
  HighlightColor,
} from './types';
import { BookFormat } from './types';
import LoadingSpinner from '../LoadingSpinner';
import { useBookMetadata } from '../../hooks/useBooks';
import { useBookmarks } from '../../hooks/useBookmarks';
import { useHighlights } from '../../hooks/useHighlights';
import { useReadingProgress } from '../../hooks/useReadingProgress';
import { useReaderSettings } from '../../hooks/useReaderSettings';
import { useAbpPermissions } from '../../contexts/AbpPermissionsContext';
import { SecureReadingProvider, useSecureReading } from '../../contexts/SecureReadingContext';
import { useDrmProtection } from '../../hooks/useDrmProtection';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import StudyToolsFAB from './StudyToolsFAB';
import { useAlert } from '../../contexts/AlertContext';
import { useEpubLayout } from '../../hooks/useEpubLayout';
import { LayoutMode, PageAnimation } from './types';
import { createCustomArchiveLoader, VIRTUAL_ROOT } from './customArchiveLoader';
import { useEpubJsManifest } from './useEpubJsManifest';
import { applyIframeDrmPolicies } from './applyIframeDrmPolicies';
import { createTouchGestureHandler } from './registerIframeTouchGestures';
import { createReaderSettingsHook } from './registerIframeReaderSettings';
import { createKeyboardNavHandler } from './registerIframeKeyboardNav';
import { epubjsService } from '../../api/epubjsService';
import { booksService } from '../../api/booksService';
import { PageTransition } from './PageTransition';
import { useIframeSelection } from './useIframeSelection';
import TextSelectionMenuWithFlashcard from './TextSelectionMenuWithFlashcard';
import { useActiveChunkIdTracker, makeChunkKey } from './useActiveChunkIdTracker';

// EPUB.js navigation item type (not exported directly)
interface NavItem {
  id: string;
  label: string;
  href: string;
  subitems?: NavItem[];
}

interface UnifiedEpubRendererProps {
  bookId: string;
  isDrmEnabled?: boolean;
}

/**
 * Resolve the 0-based spine index from a CFI string.
 * CFI format: epubcfi(/6/N[idref]!/...)  where N == (spineIndex + 1) * 2.
 * Returns null when the CFI can't be parsed or the index is out of range.
 */
function resolveSpineIndexFromCfi(
  cfi: string,
  manifest: { spineItems: { spineIndex: number }[] }
): number | null {
  // CFI format: epubcfi(/6/N[idref]!/...)  where N == (spineIndex+1)*2
  const stepMatch = cfi.match(/\/6\/(\d+)/);
  if (!stepMatch) return null;
  const step = parseInt(stepMatch[1], 10);
  const idx = step / 2 - 1;
  if (idx < 0 || idx >= manifest.spineItems.length) return null;
  return idx;
}

// Phase 4: SVG fill styles for highlights, keyed by the HighlightColor enum
// value (1–6 supported; 7–10 fall back to yellow until TextSelectionMenuWithFlashcard
// exposes them). Inline fills use rgba with reduced alpha so text under the
// overlay stays legible; mix-blend-mode: multiply makes dark text not wash out.
// useHighlights maps backend's integer HighlightColor enum to frontend hex
// strings on read, so `highlight.color` is always a hex like "#4CAF50" at
// this layer — NOT a number. Key the overlay-fill map by hex.
const HIGHLIGHT_RGBA: Record<string, string> = {
  '#FFEB3B': 'rgba(255, 235, 59, 0.35)',   // Yellow
  '#4CAF50': 'rgba(76, 175, 80, 0.35)',    // Green
  '#2196F3': 'rgba(33, 150, 243, 0.35)',   // Blue
  '#E91E63': 'rgba(233, 30, 99, 0.35)',    // Pink
  '#9C27B0': 'rgba(156, 39, 176, 0.35)',   // Purple
  '#FF9800': 'rgba(255, 152, 0, 0.35)',    // Orange
};
const DEFAULT_HIGHLIGHT_FILL = HIGHLIGHT_RGBA['#FFEB3B'];
function buildHighlightStyles(color: string | number): Record<string, string> {
  // Defensive: accept both hex strings (the expected form) and legacy
  // integer enums (in case older cached data still surfaces).
  const key = typeof color === 'number' ? integerEnumToHex(color) : color;
  return {
    fill: HIGHLIGHT_RGBA[key] ?? DEFAULT_HIGHLIGHT_FILL,
    'mix-blend-mode': 'multiply',
  };
}
function integerEnumToHex(n: number): string {
  switch (n) {
    case 1: return '#FFEB3B';
    case 2: return '#4CAF50';
    case 3: return '#2196F3';
    case 4: return '#E91E63';
    case 5: return '#9C27B0';
    case 6: return '#FF9800';
    default: return '#FFEB3B';
  }
}

/**
 * Server returns `context` pre-formatted like "...before match after..." but
 * without a consistent delimiter around the match. Robust parse: find the
 * query substring (case-insensitive) and split on that. Falls back to putting
 * everything in `match` when the query can't be located (which happens for
 * fuzzy-tokenized matches or when the occurrence is truncated at a word
 * boundary).
 */
function splitContext(
  context: string | undefined,
  query: string
): { before: string; match: string; after: string } {
  const ctx = context ?? '';
  if (!ctx || !query) return { before: '', match: ctx, after: '' };
  const idx = ctx.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return { before: '', match: ctx, after: '' };
  return {
    before: ctx.substring(0, idx),
    match: ctx.substring(idx, idx + query.length),
    after: ctx.substring(idx + query.length),
  };
}

/**
 * Internal component that contains the actual EPUB reader logic
 */
function UnifiedEpubRendererInternal({ bookId, isDrmEnabled = false }: UnifiedEpubRendererProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useAlert();

  // DRM session management. useSecureReading() is always called (Rules of Hooks);
  // the outer UnifiedEpubRenderer unconditionally wraps in SecureReadingProvider.
  // We null-out the context when DRM is not enabled so downstream checks work
  // the same way they did in the previous conditional-hook version.
  const secureReading = useSecureReading();
  const drmContext = isDrmEnabled ? secureReading : null;

  // Apply DRM protections when DRM is enabled
  // Note: We allow text selection for highlights/notes, but disable copy/paste via keyboard shortcuts
  useDrmProtection({
    disableRightClick: isDrmEnabled,
    disableTextSelection: false, // Allow selection for highlights/notes
    disableKeyboardShortcuts: isDrmEnabled,
    detectDevTools: isDrmEnabled,
    disablePrint: isDrmEnabled,
    onDevToolsDetected: isDrmEnabled ? () => {
      console.error('DevTools detected! Navigating away...');
      navigate('/');
    } : undefined,
    onViolationAttempt: isDrmEnabled ? (type) => {
      console.warn(`DRM violation attempt: ${type}`);
    } : undefined,
  });

  // Initialize DRM session if enabled
  useEffect(() => {
    if (isDrmEnabled && drmContext && !drmContext.session) {
      drmContext.initiateSession(bookId).catch((err) => {
        console.error('Failed to initiate DRM session:', err);
      });
    }
  }, [isDrmEnabled, bookId, drmContext]);
  // EPUB-specific state
  const [book, setBook] = useState<Book | null>(null);
  const [rendition, setRendition] = useState<Rendition | null>(null);
  const [currentCFI, setCurrentCFI] = useState<string>('');
  // Chapter label is derived from up to three sources, in priority order:
  //   1. TOC entry whose href matches the current spine item (best — matches
  //      what the user sees in the Contents panel).
  //   2. First h1/h2 scraped from the rendered iframe content (fallback for
  //      EPUBs without a TOC — common for quick test exports).
  //   3. Spine-index-based "Section N" (absolute last resort).
  // Each source updates its own state; the displayed label is the first
  // non-null value. Keeping them separate avoids ordering issues between the
  // `relocated` and `rendered` rendition events, which fire in different
  // orders depending on navigation type (fresh load vs. in-section page turn
  // vs. spine boundary crossing).
  const [tocChapterLabel, setTocChapterLabel] = useState<string | null>(null);
  const [contentChapterLabel, setContentChapterLabel] = useState<string | null>(null);
  const [spineFallbackLabel, setSpineFallbackLabel] = useState<string>('Chapter 1');
  const currentChapter = tocChapterLabel ?? contentChapterLabel ?? spineFallbackLabel;
  // Legacy single-setter alias — callers outside the derivation path still
  // read `currentChapter` as a string, which continues to work.
  const setCurrentChapter = setSpineFallbackLabel;
  const [totalLocations, setTotalLocations] = useState<number>(0);
  const [currentLocationIndex, setCurrentLocationIndex] = useState<number>(0);

  // Flashcard Library support: the SecureContentChunkId Guid is not surfaced
  // by the epub.js manifest endpoint, so we harvest it opportunistically from
  // /search-in-book responses (which DO include it per chapter). The tracker
  // maps chapterIndex (== spine index) → secureContentChunkId, and this memo
  // resolves the Guid for whatever spine item the user is currently on.
  const chunkIdTracker = useActiveChunkIdTracker();
  // End-of-visible-range marker. In Spread mode this is typically
  // currentLocationIndex + 1; in Single / Scrolling it equals currentLocationIndex.
  const [currentEndLocationIndex, setCurrentEndLocationIndex] = useState<number>(0);
  const [isReady, setIsReady] = useState(false);
  const [locationsReady, setLocationsReady] = useState(false);
  const [tableOfContents, setTableOfContents] = useState<TOCItem[]>([]);
  const [toolbarVisible, setToolbarVisible] = useState(true);
  const handleToggleToolbar = () => setToolbarVisible((v) => !v);

  const viewerRef = useRef<HTMLDivElement>(null);

  // Fetch book metadata
  const { data: metadata, isLoading } = useBookMetadata(bookId);

  // Fetch bookmarks and highlights from backend
  const {
    bookmarks,
    createBookmark,
    deleteBookmark,
    updateBookmark,
  } = useBookmarks(bookId);

  const {
    highlights: notes,
    createHighlight,
    deleteHighlight,
    updateHighlight,
    createPageNote,
    isCreatingPageNote,
  } = useHighlights(bookId);

  // Reading progress with auto-save
  // Get ABP settings (including progress save interval)
  const { getSetting } = useAbpPermissions();
  const progressSaveInterval = parseInt(
    getSetting('learnleaf.ProgressSaveIntervalSeconds', '30'),
    10
  );

  const {
    progress: savedProgress,
    saveProgress,
    saveProgressOnPageChange,
    startAutoSave,
    stopAutoSave,
  } = useReadingProgress(bookId, progressSaveInterval);

  // Reader settings (global per user)
  const { settings } = useReaderSettings();

  const layout = useEpubLayout({
    book,
    rendition,
    currentCFI,
    layoutMode: settings?.layoutMode ?? LayoutMode.Auto,
    pageMargin: settings?.pageMargin ?? 0,
    locationsReady,
    totalLocations,
    currentLocationIndex,
    currentEndLocationIndex,
  });

  const isScrolling = layout.effectiveMode === LayoutMode.Scrolling;

  const pageAnimation = isScrolling
    ? PageAnimation.None
    : settings?.pageAnimation ?? PageAnimation.Slide;

  // Phase 1b: manifest must load before we can build the virtual EPUB.
  const {
    manifest,
    error: manifestError,
    isLoading: manifestLoading,
  } = useEpubJsManifest(bookId);

  // Harvest secureContentChunkId per spine item from the manifest response
  // the moment it arrives, so the Flashcard Library panel can resolve the
  // currently-viewed spine to a concrete chunk Guid without waiting for a
  // /search-in-book call. Spine key == `ch:${spineIndex}` to match
  // resolveForKey consumers below.
  useEffect(() => {
    if (!manifest) return;
    chunkIdTracker.harvestChapterList(
      manifest.spineItems,
      (spine) => makeChunkKey('chapter', spine.spineIndex)
    );
  }, [manifest, chunkIdTracker.harvestChapterList]);

  // Active chunk Guid for the Flashcard Library panel. Spine index is
  // derivable from currentCFI + manifest; the Guid itself comes from the
  // chunkIdTracker (harvested from the manifest + /search-in-book responses).
  const activeSecureContentChunkId = useMemo(() => {
    if (!manifest || !currentCFI) return null;
    const spineIndex = resolveSpineIndexFromCfi(currentCFI, manifest);
    const key = makeChunkKey('chapter', spineIndex);
    return chunkIdTracker.resolveForKey(key);
  }, [manifest, currentCFI, chunkIdTracker.mapping, chunkIdTracker.resolveForKey]);

  // Phase 4 Chunk A: capture long-press text selection inside spine iframes.
  const iframeSelection = useIframeSelection(rendition, {
    onUnsupportedSelection: (reason) => {
      if (reason === 'cross-spine') {
        showToast(
          "Highlights across chapters aren't supported yet — try selecting within one chapter.",
          { type: 'info' }
        );
      } else if (reason === 'cfi-parse-failed') {
        showToast('Could not anchor highlight to that selection', { type: 'error' });
      }
    },
  });

  // Ref capturing the values the EPUB-init cleanup needs to save final progress.
  // Keeping these out of the init useEffect's dep array prevents the Book from
  // being destroyed and reconstructed on every page turn.
  const cleanupRef = useRef({
    currentCFI,
    currentChapter,
    currentLocationIndex,
    totalLocations,
    saveProgress,
  });
  useEffect(() => {
    cleanupRef.current = {
      currentCFI,
      currentChapter,
      currentLocationIndex,
      totalLocations,
      saveProgress,
    };
  });

  // Stable ref so iframe hooks always read the latest settings without
  // triggering Book re-init when settings change.
  const settingsRef = useRef(settings);
  useEffect(() => {
    settingsRef.current = settings;
  });

  // Phase 4: ref kept in sync with the `notes` array so the content-hook
  // callback (registered once at Book init) always sees the latest highlights.
  const highlightsRef = useRef<typeof notes>(notes);
  useEffect(() => {
    highlightsRef.current = notes;
  });

  // Phase 4: refs for the mutation + toast hook so the annotation click
  // callbacks (captured in closure at annotations.add time) always reach
  // the current React tree's functions without stale-closure weirdness.
  const deleteHighlightRef = useRef(deleteHighlight);
  const showToastRef = useRef(showToast);
  useEffect(() => {
    deleteHighlightRef.current = deleteHighlight;
    showToastRef.current = showToast;
  });

  // Phase 4: click-on-existing-highlight handler. Clicking the SVG overlay
  // opens a native confirm asking whether to delete. This is the MVP for
  // the D6 deferral — full edit-in-menu (change color / note) comes later.
  // Captured by reference so subsequent annotations.add calls share the
  // same stable handler (and so closure drift doesn't accumulate).
  const onHighlightClick = useCallback((highlightId: string) => {
    const h = highlightsRef.current?.find((x) => x.id === highlightId);
    const preview =
      ((h?.selectedText ?? '').substring(0, 60).trim()) || 'this highlight';
    if (!window.confirm(`Delete highlight "${preview}"?`)) return;
    (deleteHighlightRef.current as any)(highlightId).catch((err: any) => {
      console.error('[UnifiedEpubRenderer] deleteHighlight failed:', err);
      showToastRef.current('Could not delete highlight', { type: 'error' });
    });
  }, []);

  // Initialize EPUB.js with the custom archive loader (Phase 1b).
  useEffect(() => {
    if (!viewerRef.current) return;
    if (!manifest) return; // wait for the manifest pre-fetch

    const requestMethod = createCustomArchiveLoader({ bookId, manifest });

    // openAs: "directory"       → epub.js won't try to unzip a .epub (JSZip off-path).
    // requestMethod             → our loader serves container.xml, OPF, spine, assets.
    // replacements: "blobUrl"   → iframe-embedded <img>/<link>/<url(...)> get blob: URLs
    //                              whose bytes flow through requestMethod, so no
    //                              unauthenticated absolute URLs ever leak into the DOM.
    const newBook = ePub(VIRTUAL_ROOT(bookId), {
      openAs: 'directory',
      requestMethod,
      replacements: 'blobUrl',
    } as any);
    // ^ `as any`: epub.js types/book.d.ts omits `openAs` and `replacements` from
    // BookOptions even though they're documented and supported at runtime. Phase 4
    // or a later hardening pass can add a local .d.ts augmentation to drop the cast.

    newBook.ready.then(() => {
      const newRendition = newBook.renderTo(viewerRef.current!, {
        width: '100%',
        height: '100%',
      });

      // Phase 2: wire DRM policies into each spine-item iframe as it mounts.
      if (isDrmEnabled) {
        newRendition.hooks.content.register(applyIframeDrmPolicies);
      }

      // Phase 3: iframe touch gestures — swipe page turn, long-press, pinch zoom,
      // and tap-zone detection (for touch devices where the overlay is transparent).
      newRendition.hooks.content.register(
        createTouchGestureHandler(newRendition, {
          onLongPress: (ev) => {
            window.dispatchEvent(new CustomEvent('epubjs:iframe-longpress', { detail: ev }));
          },
        })
      );

      // Phase 3: iframe reader-settings propagation (margin, animation).
      newRendition.hooks.content.register(
        createReaderSettingsHook(() => ({
          pageMargin: (settingsRef.current?.pageMargin ?? 0) as any,
          pageAnimation: (settingsRef.current?.pageAnimation ?? 0) as any,
          tapZoneMode: (settingsRef.current?.tapZoneMode ?? 0) as any,
        }))
      );

      // Phase 4: re-attach persisted highlights whenever a spine iframe mounts.
      // epub.js does not persist annotations across spine unloads, so this hook
      // is the canonical re-attach point. `highlightsRef.current` gives fresh
      // access without re-registering the hook on every highlight change.
      newRendition.hooks.content.register((contents: any) => {
        const currentHighlights = highlightsRef.current ?? [];
        const sectionIndex: number | undefined = contents?.sectionIndex;
        for (const h of currentHighlights) {
          if (!h?.location) continue;
          // Cheap section-scoping filter: CFIs we emit include the spine step
          // as /6/N where N == (sectionIndex + 1) * 2. If the section index is
          // available on Contents, skip highlights that don't match this spine.
          if (sectionIndex != null) {
            const expectedStep = (sectionIndex + 1) * 2;
            if (!h.location.startsWith(`epubcfi(/6/${expectedStep}`)) continue;
          }
          try {
            newRendition.annotations.add(
              'highlight',
              h.location,
              { highlightId: h.id, color: h.color, noteText: h.noteText },
              () => onHighlightClick(h.id),
              `ep-hl-color-${h.color}`,
              buildHighlightStyles(h.color)
            );
          } catch (err) {
            // Unparseable CFI — skip, don't crash the reader.
            console.warn('[UnifiedEpubRenderer] highlight attach failed', h.id, err);
          }
        }
      });

      // Phase 6: keyboard navigation inside each spine iframe.
      newRendition.hooks.content.register(
        createKeyboardNavHandler(newRendition as any)
      );

      // Scroll-forward: in Scrolling mode (flow: scrolled-doc) epub.js sizes the
      // iframe to match the full content height, so the iframe's own document has
      // nothing to scroll and wheel events originating inside it are effectively
      // dead. The outer .epub-container is the real scroll target (overflow: auto,
      // viewport-sized). Forward deltaY from the iframe's document to that
      // container whenever it's actually scrollable — in paginated modes
      // scrollHeight === clientHeight and this is a safe no-op.
      //
      // Same hook also does a best-effort chapter-label derivation: if the EPUB
      // has no TOC (book.navigation is empty), the label falls back to a spine
      // index like "Section 7" which is meaningless to a reader. Scraping the
      // first h1/h2 from the rendered content gives the actual chapter title
      // ("Chapter One" for the test book), which is what the user expects to
      // see at the bottom of the reader.
      newRendition.hooks.content.register((contents: any) => {
        const doc: Document | undefined = contents?.document;
        if (!doc) return;

        // Wheel forwarding (Scrolling mode).
        const onWheel = (e: WheelEvent) => {
          const container = viewerRef.current?.querySelector('.epub-container') as HTMLElement | null;
          if (container && container.scrollHeight > container.clientHeight) {
            container.scrollTop += e.deltaY;
            e.preventDefault();
          }
        };
        doc.addEventListener('wheel', onWheel, { passive: false });

        // Content-derived chapter label. Writes its own state; the displayed
        // label is `tocChapterLabel ?? contentChapterLabel ?? spineFallbackLabel`
        // so TOC always wins when present and this is pure fallback when the
        // book has no nav doc. Clear first so the old section's heading
        // doesn't bleed into the new one if no heading is found here.
        setContentChapterLabel(null);
        const headingEl = doc.querySelector('h1, h2') as HTMLElement | null;
        const headingText = headingEl?.textContent?.trim();
        if (headingText) {
          setContentChapterLabel(headingText);
        }
      });

      newRendition.display().then(() => {
        setIsReady(true);
      });

      // Phase 3 Chunk A: consume server-generated CFI locations (byte-exact
      // match against epub.js book.locations.generate(1024) verified at 100%
      // on the Twelve-Months test book). The client-side fallback to
      // locations.generate() has been intentionally removed: it fires 60+
      // parallel spine fetches for a full-book walk, which both wastes
      // bandwidth and defeats the DRM posture of never loading the whole
      // book to the browser at once. Any book that reaches the reader MUST
      // have server-generated locations; the Hangfire job runs on every
      // EpubJs upload, so the only way to hit the null path is a book
      // uploaded before Phase 3 Chunk A landed. For those, show a clean
      // error and surface a recovery path instead of silently falling back.
      if (manifest.cfiLocations && manifest.cfiLocations.length > 0) {
        newBook.locations.load(manifest.cfiLocations);
        setTotalLocations(manifest.cfiLocations.length);
        setLocationsReady(true);
      } else {
        console.error(
          '[UnifiedEpubRenderer] manifest.cfiLocations missing — book needs CFI backfill. ' +
            'Triggering locations.generate() on the client is disabled (would fire 60+ ' +
            'parallel spine fetches). Admin: re-run GenerateCfiLocationsJob for this book.'
        );
        // Render-side surfacing happens through the existing setLocationsReady
        // staying false — the pagination widget shows "--" rather than "1/N".
        // Consider a future UX improvement: dedicated "Reindexing, try again
        // in a minute" state.
      }

      // Extract ToC. Phase 1b's synthesized nav gives a flat list; Phase 4 replaces
      // this with the real extracted nav doc.
      newBook.loaded.navigation.then((nav) => {
        const toc: TOCItem[] = convertNavToTOC(nav.toc);
        setTableOfContents(toc);
      });

      // Track location changes.
      newRendition.on('relocated', (location: any) => {
        if (location.start) {
          setCurrentCFI(location.start.cfi);
          setCurrentLocationIndex(location.start.location || 0);
          // In Spread mode epub.js reports both the left (start) and right (end)
          // columns — remember the end so the bottom bar can show "6–7 / 815"
          // rather than just "6" when two pages are visible. In Single / Scrolling
          // mode start === end and the range collapses to a single number.
          setCurrentEndLocationIndex(
            typeof location.end?.location === 'number'
              ? location.end.location
              : location.start.location || 0
          );
          // Chapter label feeds three priority-ranked states (see useState
          // declarations). Relocated fills TOC + spine fallback here; the
          // content-heading state is filled by the rendition.hooks.content
          // hook below when the iframe first renders.
          const href = location.start.href as string | undefined;
          const tocEntry = href ? newBook.navigation?.get(href) : null;
          if (tocEntry && typeof tocEntry.label === 'string' && tocEntry.label.trim()) {
            setTocChapterLabel(tocEntry.label.trim());
          } else {
            // No TOC hit — clear any stale TOC label from a previous section.
            setTocChapterLabel(null);
          }
          if (href) {
            const spineItem = newBook.spine.get(href);
            if (spineItem) {
              setSpineFallbackLabel(
                spineItem.index != null ? `Section ${spineItem.index + 1}` : 'Unknown'
              );
            }
          }
        }
      });

      setBook(newBook);
      setRendition(newRendition);
    });

    return () => {
      const snap = cleanupRef.current;
      if (snap.currentCFI && rendition) {
        snap.saveProgress(
          snap.currentCFI,
          snap.currentChapter,
          (snap.currentLocationIndex / (snap.totalLocations || 1)) * 100
        );
      }
      if (rendition) rendition.destroy();
      if (newBook) newBook.destroy();
    };
  }, [bookId, manifest, isDrmEnabled]);

  // Phase 4: keep rendition annotations in sync with the `notes` (highlights)
  // array. The content-hook (Task B1) handles freshly-mounted spines; this
  // effect handles create/delete into already-mounted spines.
  const attachedHighlightIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!rendition) return;
    const currentIds = new Set((notes ?? []).map((h) => h.id));
    const attached = attachedHighlightIdsRef.current;

    // Remove annotations whose highlight no longer exists locally.
    for (const prevId of attached) {
      if (currentIds.has(prevId)) continue;
      try {
        // epub.js exposes the internal map at rendition.annotations._annotations;
        // key = `${cfiRange}${type}`. We stored highlightId in data so we can
        // find by id.
        const allAnn = (rendition.annotations as any)._annotations ?? {};
        for (const key of Object.keys(allAnn)) {
          const ann = allAnn[key];
          if (ann?.data?.highlightId === prevId) {
            rendition.annotations.remove(ann.cfiRange, ann.type);
          }
        }
      } catch {
        // Internal shape drift in a future epub.js version — the next full
        // book re-open + B1 re-attach will converge state.
      }
    }

    // Add annotations for newly-appeared highlights.
    for (const h of notes ?? []) {
      if (attached.has(h.id)) continue;
      if (!h?.location) continue;
      try {
        rendition.annotations.add(
          'highlight',
          h.location,
          { highlightId: h.id, color: h.color, noteText: h.noteText },
          () => onHighlightClick(h.id),
          `ep-hl-color-${h.color}`,
          buildHighlightStyles(h.color)
        );
      } catch {
        // Unparseable CFI — skip.
      }
    }

    attachedHighlightIdsRef.current = currentIds;
  }, [rendition, notes]);

  // Restore last reading position ONCE per book.
  // Guarded so later cache updates from save mutations don't jump the user back.
  const restoredBookIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (restoredBookIdRef.current === bookId) return;
    if (!savedProgress || !rendition || !book?.locations) return;

    restoredBookIdRef.current = bookId;

    const savedCFI = String(savedProgress.currentLocation);
    if (savedCFI && savedCFI !== currentCFI) {
      rendition.display(savedCFI).catch(error => {
        console.error('Failed to restore reading position:', error);
      });
    }
  }, [savedProgress, rendition, book, bookId]);

  // Phase 3: first-paint prefetch — fire 3 parallel spine fetches centered on the
  // resume index, so the first page paint and the first prev/next tap resolve
  // from the axios cache / epub.js's own cache instead of hitting the network.
  useEffect(() => {
    if (!manifest || !savedProgress?.currentLocation) return;
    const resumeIdx = resolveSpineIndexFromCfi(String(savedProgress.currentLocation), manifest);
    if (resumeIdx == null) return;
    const targets = [resumeIdx - 1, resumeIdx, resumeIdx + 1].filter(
      (i) => i >= 0 && i < manifest.spineItems.length
    );
    Promise.all(targets.map((i) => epubjsService.getSpineItem(bookId, i))).catch(() => {
      // Best-effort prefetch — swallow errors.
    });
  }, [bookId, manifest, savedProgress?.currentLocation]);

  // Start auto-save
  useEffect(() => {
    if (!rendition) return;

    const cleanup = startAutoSave(
      () => currentCFI || '',
      () => currentChapter,
      () => (totalLocations ? (currentLocationIndex / totalLocations) * 100 : 0)
    );

    return cleanup;
  }, [rendition, startAutoSave, currentCFI, currentChapter, currentLocationIndex, totalLocations]);

  // Save progress immediately when location changes (user navigates to new page/chapter)
  const isInitialMount = useRef(true);
  useEffect(() => {
    // Skip on initial mount (we just loaded the saved progress)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (rendition && currentCFI && totalLocations > 0) {
      saveProgressOnPageChange(
        currentCFI,
        currentChapter,
        (currentLocationIndex / totalLocations) * 100
      );
    }
  }, [currentCFI]); // Only depend on currentCFI (the actual location)

  // Convert EPUB navigation to TOC format
  const convertNavToTOC = (navItems: NavItem[], level: number = 0): TOCItem[] => {
    return navItems.map((item, index) => ({
      id: `${level}-${index}`,
      label: item.label,
      href: item.href,
      cfi: item.href,
      level,
      children: item.subitems ? convertNavToTOC(item.subitems, level + 1) : undefined,
    }));
  };

  // Convert EPUB state to unified navigation state
  const navigation: NavigationState = useMemo(
    () => ({
      currentLocation: currentLocationIndex,
      currentLocationLabel: currentChapter,
      totalLocations: totalLocations || 1,
      progressPercentage: totalLocations
        ? (currentLocationIndex / totalLocations) * 100
        : 0,
      canNavigatePrev: currentLocationIndex > 0,
      canNavigateNext: currentLocationIndex < totalLocations - 1,
      currentPage: layout.currentPage,
      currentEndPage: layout.currentEndPage,
      totalPages: layout.totalPages,
      effectiveLayoutMode: layout.effectiveMode,
    }),
    [currentLocationIndex, currentChapter, totalLocations, layout.currentPage, layout.currentEndPage, layout.totalPages, layout.effectiveMode]
  );

  // Navigation callbacks
  const navigationCallbacks: NavigationCallbacks = useMemo(
    () => ({
      onNavigatePrev: () => {
        rendition?.prev();
      },
      onNavigateNext: () => {
        rendition?.next();
      },
      onNavigateToLocation: (location) => {
        if (typeof location === 'number' && book?.locations) {
          const cfi = book.locations.cfiFromLocation(location);
          rendition?.display(cfi);
        }
      },
      onNavigateToTOC: (item) => {
        if (item.cfi || item.href) {
          rendition?.display(item.cfi || item.href!);
        }
      },
      onNavigateToBookmark: (bookmark) => {
        if (typeof bookmark.location === 'string') {
          rendition?.display(bookmark.location);
        }
      },
      onNavigateToNote: (note) => {
        console.log('EPUB: Navigating to note location:', note.location);
        if (typeof note.location === 'string') {
          rendition?.display(note.location);
        }
      },
      onNavigateToSearchResult: (result) => {
        if (typeof result.location === 'string') {
          rendition?.display(result.location);
        }
      },
    }),
    [rendition, book]
  );

  // Reader actions
  const actions: ReaderActions = useMemo(
    () => ({
      onCreateBookmark: async (location: string | number, note?: string) => {
        const cfi = typeof location === 'string' ? location : currentCFI;
        await createBookmark({
          location: cfi,
          locationLabel: currentChapter,
          note,
        });
      },
      onDeleteBookmark: async (bookmarkId: string) => {
        await deleteBookmark(bookmarkId);
      },
      onUpdateBookmark: async (bookmarkId: string, note: string) => {
        await updateBookmark({ id: bookmarkId, note });
      },
      onCreateNote: async (
        location: string | number,
        selectedText: string,
        color: HighlightColor,
        noteText?: string
      ) => {
        const cfi = typeof location === 'string' ? location : currentCFI;
        await createHighlight({
          location: cfi,
          locationLabel: currentChapter,
          selectedText,
          color,
          noteText,
        });
        // TODO: Add visual highlight to rendition
      },
      onDeleteNote: async (noteId: string) => {
        await deleteHighlight(noteId);
        // TODO: Remove visual highlight from rendition
      },
      onUpdateNote: async (noteId: string, noteText: string) => {
        // Find the note to get its current color
        const note = notes.find(n => n.id === noteId);
        if (note) {
          await updateHighlight({
            id: noteId,
            color: note.color,
            noteText
          });
        }
        // TODO: Update visual highlight in rendition
      },
      onSearch: async (query: string): Promise<SearchResult[]> => {
        if (!query || query.trim().length === 0) return [];

        try {
          const response = await booksService.searchInBook(bookId, query, 50);
          // Harvest chapterIndex → secureContentChunkId mappings for the
          // Flashcard Library panel (see useActiveChunkIdTracker).
          chunkIdTracker.harvestSearchResponse(response);
          const flat: SearchResult[] = [];

          for (const chunk of response.results ?? []) {
            const spineIdx = chunk.chapterIndex ?? 0;
            // Spine-base CFI pointing at the chapter root. Exact character-offset
            // navigation is deferred (see docs/dual-renderer-pending-issues.md §2).
            const cfi = `epubcfi(/6/${(spineIdx + 1) * 2}!/4)`;
            const label = chunk.chapterTitle?.trim()
              ? chunk.chapterTitle
              : `Chapter ${spineIdx + 1}`;

            for (const occ of chunk.occurrences ?? []) {
              const { before, match, after } = splitContext(occ.context, query);
              flat.push({
                id: `${chunk.secureContentChunkId}-${occ.characterOffset}`,
                location: cfi,
                locationLabel: label,
                excerpt: match || query,
                beforeContext: before,
                afterContext: after,
              });
            }
          }

          return flat;
        } catch (err) {
          console.error('[UnifiedEpubRenderer] searchInBook failed:', err);
          showToast('Search failed — please try again', { type: 'error' });
          return [];
        }
      },
    }),
    [
      book,
      rendition,
      currentCFI,
      currentChapter,
      createBookmark,
      deleteBookmark,
      updateBookmark,
      createHighlight,
      deleteHighlight,
      updateHighlight,
      notes,
      chunkIdTracker,
    ]
  );

  // Handle adding page-level notes
  const handleAddPageNote = useCallback(
    async (noteText: string, color: HighlightColor) => {
      try {
        await createPageNote({
          location: currentCFI,
          locationLabel: currentChapter,
          color,
          noteText,
        });
      } catch (error) {
        console.error('Failed to create page note:', error);
        showToast('Failed to create page note. Please try again.', { type: 'error' });
      }
    },
    [createPageNote, currentCFI, currentChapter]
  );

  if (manifestError) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <p className="text-red-600 text-center">
          Failed to load EPUB manifest: {manifestError.message}
        </p>
      </div>
    );
  }
  if (manifestLoading || !manifest) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-900">
        <LoadingSpinner />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-900">
        <LoadingSpinner />
      </div>
    );
  }
  // Note: intentionally NOT gating on `!isReady` here. The viewer <div ref={viewerRef}>
  // only lives inside the main return below; if we early-return until isReady is true,
  // the ref never attaches and the book-init useEffect's `if (!viewerRef.current) return;`
  // guard bails every render — a catch-22 that produces an infinite spinner. Instead we
  // render the main return immediately so the ref attaches, and overlay a spinner on
  // top of the viewer while !isReady.

  // Gate content behind DRM initialization when DRM is enabled
  // This ensures security protections are fully active before content is visible
  // Skip DRM session check when offline - content will be loaded from IndexedDB
  if (isDrmEnabled && drmContext && !drmContext.session && navigator.onLine) {
    // Show error if session initialization failed
    if (drmContext.error) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-neutral-900 text-white">
          <div className="text-error-500 text-6xl mb-4">⚠️</div>
          <p className="text-xl font-semibold mb-2">Failed to Initialize Secure Session</p>
          <p className="text-sm text-neutral-400 mb-6 max-w-md text-center">{drmContext.error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      );
    }

    // Show loading while initializing
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-neutral-900 text-white">
        <LoadingSpinner />
        <p className="mt-4 text-lg">Initializing secure session...</p>
        <p className="mt-2 text-sm text-neutral-400">Setting up DRM protections</p>
      </div>
    );
  }

  return (
    <UnifiedReaderLayout
      bookId={bookId}
      format={BookFormat.Epub}
      metadata={metadata}
      navigation={navigation}
      navigationCallbacks={navigationCallbacks}
      tableOfContents={tableOfContents}
      bookmarks={bookmarks}
      notes={notes}
      actions={actions}
      onAddPageNote={handleAddPageNote}
      theme={settings?.theme}
      fontSize={settings?.fontSize}
      fontFamily={settings?.fontFamily}
      activeSecureContentChunkId={activeSecureContentChunkId}
    >
      {/* Phase 6: skip link for keyboard users. `sr-only` hides it off-screen
          until focused; `focus:not-sr-only` brings it back in view. */}
      <a
        href="#epub-viewer-root"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:bg-white focus:shadow focus:rounded"
      >
        Skip to book content
      </a>
      {/* Phase 6: polite live region announces page changes + chapter to
          screen readers. `sr-only` keeps it off-screen; `aria-live="polite"`
          avoids interrupting ongoing narration. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {isReady && totalLocations > 0
          ? `Page ${currentLocationIndex + 1} of ${totalLocations} — ${currentChapter}`
          : ''}
      </div>
      <div className="relative w-full h-full">
        {/*
          The viewer div is NOT wrapped in PageTransition for the EpubJs
          renderer: framer-motion's <AnimatePresence mode="wait"> unmounts
          + remounts children when the `key` prop changes, which destroys
          the viewerRef target and nukes epub.js's spine iframe mid-render
          as soon as the first `relocated` event advances
          currentLocationIndex. epub.js already provides its own internal
          page-turn animation (slide/fade are controlled via rendition
          flow settings), so the outer wrapper was double-wrapping anyway.
        */}
        {/* Phase 6: skip-link target. tabIndex=-1 so it's programmatically
            focusable but not in the tab order. */}
        <div id="epub-viewer-root" tabIndex={-1} className="w-full h-full">
        <div
          ref={viewerRef}
          className="w-full h-full"
          style={{
            overflow: 'hidden',
            backgroundColor: settings?.backgroundColor || '#FFFFFF',
          }}
        />
        </div>
        {iframeSelection.isOpen && iframeSelection.selection && iframeSelection.cfi && (
          <TextSelectionMenuWithFlashcard
            selectedText={iframeSelection.selectedText ?? ''}
            selection={iframeSelection.selection}
            menuPosition={iframeSelection.menuPosition ?? { x: 0, y: 0 }}
            onCreateHighlight={async (color, noteText) => {
              try {
                await createHighlight({
                  location: iframeSelection.cfi!,
                  locationLabel: currentChapter,
                  selectedText: iframeSelection.selectedText ?? '',
                  color,
                  noteText: noteText || undefined,
                });
              } catch (err) {
                console.error('[UnifiedEpubRenderer] createHighlight failed:', err);
                showToast('Could not save highlight', { type: 'error' });
              } finally {
                iframeSelection.clear();
              }
            }}
            onCreateFlashcard={undefined as any /* unused for Phase 4 */}
            onClose={iframeSelection.clear}
            bookMetadata={metadata as any}
            locationLabel={currentChapter}
          />
        )}
        {!isReady && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-neutral-900"
            style={{ zIndex: 50 }}
          >
            <LoadingSpinner />
          </div>
        )}
      </div>
    </UnifiedReaderLayout>
  );
}

/**
 * Unified EPUB Renderer - DRM-aware wrapper
 *
 * This component automatically detects DRM status and wraps the reader
 * in SecureReadingProvider when DRM is enabled. For non-DRM books,
 * it renders the reader directly without DRM overhead.
 *
 * Features (available for ALL books):
 * - Table of Contents navigation
 * - Highlights with color picker
 * - Notes and annotations
 * - Bookmarks
 * - Reading progress tracking
 * - Reader settings (theme, font, etc.)
 * - EpubJs rendering
 *
 * DRM Features (only when isDrmEnabled):
 * - DRM quota tracking and display
 * - Content protection (no right-click, text selection, print)
 * - DevTools detection
 * - Session management with heartbeat
 */
export default function UnifiedEpubRenderer({ bookId, isDrmEnabled = false }: UnifiedEpubRendererProps) {
  // Always wrap in SecureReadingProvider so useSecureReading() inside
  // UnifiedEpubRendererInternal can be called unconditionally (Rules of Hooks).
  // The provider's session/heartbeat effects are gated on isDrmEnabled inside
  // Internal, so this carries no runtime cost for non-DRM books.
  return (
    <SecureReadingProvider>
      <UnifiedEpubRendererInternal bookId={bookId} isDrmEnabled={isDrmEnabled} />
    </SecureReadingProvider>
  );
}
